import React, { useState } from 'react';
import { Article } from '../../../types';
import { addArticle, fetchArticles, updateArticle } from '../../../features/admin/articleSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

type Props = {
    initialData: Article | null;
    onClose: () => void;
    onSuccess: () => void;
};

const ArticleModal = ({ initialData, onClose, onSuccess }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const [formData, setFormData] = useState<{
        id?: number;
        title: string;
        titleAr: string;
        country: string;
        countryAr: string;
        subTitle: string;
        subTitleAr: string;
        image: string | File;
        link: string;
        details: string;
        detailsAr: string;
    }>({
        id: initialData?.id,
        title: initialData?.title || '',
        titleAr: initialData?.titleAr || '',
        country: initialData?.country || '',
        countryAr: initialData?.countryAr || '',
        subTitle: initialData?.subTitle || '',
        subTitleAr: initialData?.subTitleAr || '',
        link: initialData?.link || '',
        details: initialData?.details || '',
        detailsAr: initialData?.detailsAr || '',
        image: initialData?.image || '',
    });

    const [imagePreview, setImagePreview] = useState<string>(
        typeof formData.image === 'string' ? formData.image : ''
    );

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData({ ...formData, image: file });
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        const data = new FormData();
        data.append("title", formData.title);
        data.append("titleAr", formData.titleAr);
        data.append("country", formData.country);
        data.append("countryAr", formData.countryAr);
        data.append("subTitle", formData.subTitle);
        data.append("subTitleAr", formData.subTitleAr);
        data.append("details", formData.details);
        data.append("detailsAr", formData.detailsAr);
        data.append("link", formData.link);
        if (formData.image instanceof File) {
            data.append("image", formData.image);
        }

        // Validation
        if (formData.title.trim().length < 5 || formData.titleAr.trim().length < 5) {
            toast.error(t("title-too-short"));
            return;
        }
        if (formData.country.trim().length < 3 || formData.countryAr.trim().length < 3) {
            toast.error(t("country-too-short"));
            return;
        }
        if (!formData.image) {
            toast.error(t("image-required"));
            return;
        }
        if (formData.subTitle.trim().length < 8 || formData.subTitleAr.trim().length < 8) {
            toast.error(t("subtitle-too-short"));
            return;
        }
        if (formData.details.trim().length < 10 || formData.detailsAr.trim().length < 10) {
            toast.error(t("details-too-short"));
            return;
        }

        try {
            if (initialData && initialData.id) {
                await dispatch(updateArticle({ id: initialData.id, data }));
            } else {
                await dispatch(addArticle(data));
            }
            await dispatch(fetchArticles());
            onSuccess();
            onClose();
        } catch (error) {
            toast.error(t("error-message"));
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 justify-center p-5 flex z-50">
            <div className="bg-white p-6 rounded-xl overflow-y-scroll max-h-[90vh] shadow-lg space-y-4 w-full max-w-2xl">
                <h2 className="text-xl font-bold text-center">
                    {initialData ? t("article-title-edit") : t("article-title-create")}
                </h2>
                
                <input name="title" value={formData.title} onChange={handleChange} placeholder={t("article-placeholder")} className="w-full border rounded px-3 py-2" />
                <input name="titleAr" value={formData.titleAr} onChange={handleChange} placeholder={t("article-placeholder-ar")} className="w-full border rounded px-3 py-2" />
                <input name="country" value={formData.country} onChange={handleChange} placeholder={t("country-placeholder")} className="w-full border rounded px-3 py-2" />
                <input name="countryAr" value={formData.countryAr} onChange={handleChange} placeholder={t("country-placeholder-ar")} className="w-full border rounded px-3 py-2" />
                <input name="subTitle" value={formData.subTitle} onChange={handleChange} placeholder={t("article-subTitle-placeholder")} className="w-full border rounded px-3 py-2" />
                <input name="subTitleAr" value={formData.subTitleAr} onChange={handleChange} placeholder={t("article-subTitle-placeholder-ar")} className="w-full border rounded px-3 py-2" />
                <input name="link" value={formData.link} onChange={handleChange} placeholder={t("article-link-placeholder")} className="w-full border rounded px-3 py-2" />
                <textarea name="details" value={formData.details} onChange={handleChange} placeholder={t("details-placeholder")} className="w-full border rounded px-3 py-2 h-28" />
                <textarea name="detailsAr" value={formData.detailsAr} onChange={handleChange} placeholder={t("details-placeholder-ar")} className="w-full border rounded px-3 py-2 h-28" />
                {/* Image Upload */}
                <div className="w-full">
                    <label htmlFor="image-upload" className="block cursor-pointer">
                        <div className="w-full h-40 border border-dashed rounded flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition">
                            {imagePreview ? (
                                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded" />
                            ) : (
                                <span className="text-gray-500">{t("click_to_select")}</span>
                            )}
                        </div>
                    </label>
                    <input id="image-upload" type="file" name="image" accept="image/*" onChange={handleImageChange} className="hidden" />
                </div>

                <div className="flex justify-between pt-4">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">{t("cancel")}</button>
                    <button onClick={handleSubmit} className="px-4 py-2 bg-primary text-white rounded">
                        {initialData ? t("update") : t("create")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ArticleModal;
